import { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/App";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getEmpresas, getEjercicios } from "@/lib/api";
import { onDataChanged } from "@/lib/events";
import { GuidedTour } from "@/components/GuidedTour";
import NotificationBell from "@/components/notifications/NotificationBell";
import { UserButton, useUser } from "@clerk/clerk-react";
import {
  Moon,
  Sun,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Search,
  Calendar,
  PieChart,
  GitCompare,
  Settings,
  Check,
  Building,
  Building2,
  Briefcase,
  AlertTriangle,
  HelpCircle,
  Download,
  ArrowRightLeft,
  ArrowUpRight,
  TrendingUp,
  Activity,
  HeartPulse,
  Home,
  Plug,
  BookOpen,
  Landmark,
  Sparkles,
  Sigma,
  LayoutGrid,
} from "lucide-react";

// URL de Pool Bancario. Misma instancia Clerk (clerk.pflabs.es), por lo que la
// sesión se mantiene al cambiar de app.
const POOLBANCARIO_URL = process.env.REACT_APP_POOLBANCARIO_URL || "https://poolbancari.pflabs.es";

export default function DashboardLayout() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  // Admin = el email del usuario está en REACT_APP_ADMIN_EMAILS (debe coincidir con
  // ADMIN_EMAILS del backend, que es quien realmente autoriza el endpoint).
  const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const userEmail = (user?.primaryEmailAddress?.emailAddress || "").toLowerCase();
  const isAdmin = adminEmails.includes(userEmail);
  const [empresaPickerOpen, setEmpresaPickerOpen] = useState(false);
  const [paletaOpen, setPaletaOpen] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);

  const loadEjercicios = async (empresaId) => {
    try {
      const res = await getEjercicios(empresaId);
      const sortedEjercicios = res.data
        .filter(e => e.total_cuentas > 0)
        .sort((a, b) => b.anio - a.anio);
      setEjercicios(sortedEjercicios);
    } catch (error) {
      console.error("Error loading ejercicios:", error);
      setEjercicios([]);
    }
  };

  const loadEmpresas = async () => {
    try {
      const res = await getEmpresas();
      setEmpresas(res.data);
    } catch (error) {
      console.error("Error loading empresas:", error);
    }
  };

  // Load empresas on mount
  useEffect(() => {
    loadEmpresas();
  }, []);

  // Load ejercicios when empresa changes
  useEffect(() => {
    if (selectedEmpresa) {
      loadEjercicios(selectedEmpresa.id);
    }
  }, [selectedEmpresa]);

  // Refrescar el menú (sin recargar la página) cuando una página crea/borra una
  // empresa o ejercicio, o importa: el shell es persistente y no se entera solo.
  useEffect(() => {
    return onDataChanged((e) => {
      const scope = e?.detail?.scope;
      if (scope !== "ejercicios") loadEmpresas();
      if (scope !== "empresas" && selectedEmpresa) loadEjercicios(selectedEmpresa.id);
    });
  }, [selectedEmpresa]);

  // Sync empresa from URL
  useEffect(() => {
    // Si no queda ninguna empresa (se borró la última), limpiar la selección
    // para que el desplegable no siga mostrando una empresa que ya no existe.
    if (empresas.length === 0) {
      if (selectedEmpresa) setSelectedEmpresa(null);
      return;
    }

    const pathParts = location.pathname.split('/');

    // Check for empresa ID in URL
    const empresaIdFromUrl = pathParts.includes('empresas') ? pathParts[pathParts.indexOf('empresas') + 1] : null;
    if (empresaIdFromUrl) {
      const empresa = empresas.find(e => e.id === empresaIdFromUrl);
      if (empresa && (!selectedEmpresa || selectedEmpresa.id !== empresa.id)) {
        setSelectedEmpresa(empresa);
        return;
      }
    }

    // Check for ejercicio ID in URL - need to find which empresa it belongs to
    const ejercicioIdFromUrl = pathParts.includes('ejercicios') ? pathParts[pathParts.indexOf('ejercicios') + 1] : null;
    if (ejercicioIdFromUrl) {
      const ejercicioInCurrent = ejercicios.find(e => e.id === ejercicioIdFromUrl);
      if (ejercicioInCurrent) {
        return;
      }

      // Ejercicio not in current list, search in all empresas
      const findEmpresaForEjercicio = async () => {
        for (const empresa of empresas) {
          try {
            const res = await getEjercicios(empresa.id);
            const found = res.data.find(e => e.id === ejercicioIdFromUrl);
            if (found) {
              setSelectedEmpresa(empresa);
              return;
            }
          } catch (error) {
            console.error("Error finding empresa:", error);
          }
        }
      };
      findEmpresaForEjercicio();
      return;
    }

    // Sin empresa/ejercicio en la URL: seleccionar la primera si no hay ninguna
    // seleccionada O si la que había ya no existe en la lista (p. ej. se acaba
    // de borrar) — así el desplegable nunca muestra una empresa borrada.
    const seleccionValida =
      selectedEmpresa && empresas.some((e) => e.id === selectedEmpresa.id);
    if (!seleccionValida) {
      setSelectedEmpresa(empresas[0]);
    }
  }, [location.pathname, empresas, ejercicios]);

  // Paleta de comandos: Ctrl+K / Cmd+K desde cualquier pantalla.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletaOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSelectEmpresa = async (empresa) => {
    setSelectedEmpresa(empresa);
    if (location.pathname === "/inicio") return;

    // Navigate to the new empresa's page or first ejercicio
    try {
      const res = await getEjercicios(empresa.id);
      const ejerciciosConDatos = res.data
        .filter(e => e.total_cuentas > 0)
        .sort((a, b) => b.anio - a.anio);

      if (ejerciciosConDatos.length > 0) {
        // Navigate to the first (most recent) ejercicio's analysis page
        navigate(`/ejercicios/${ejerciciosConDatos[0].id}/analisis?tab=dashboard`);
      } else {
        // Navigate to empresa detail if no ejercicios with data
        navigate(`/empresas/${empresa.id}`);
      }
    } catch (error) {
      console.error("Error loading ejercicios for new empresa:", error);
      navigate(`/empresas/${empresa.id}`);
    }
  };

  // Check current paths
  const pathParts = location.pathname.split('/');
  const currentEjercicioId = pathParts.includes('ejercicios') ? pathParts[pathParts.indexOf('ejercicios') + 1] : null;
  const isComparativaRatiosActive = /\/empresas\/[^/]+\/comparativa(?:$|\/|\?)/.test(location.pathname);
  const isComparativaAniosActive = location.pathname.includes('/comparativa-anios');
  const isComparativaFlujosActive = location.pathname.includes('/comparativa-flujos');
  const isResumenActive = location.pathname.includes('/resumen');
  const isProyeccionActive = location.pathname.includes('/proyeccion');
  const isSaludActive = location.pathname.includes('/salud');
  const isInicioActive = location.pathname === '/inicio';
  const isFinoviewActive = location.pathname.includes('/finoview');
  const isHoldedActive = location.pathname.includes('/holded');
  const isEmpresasListPage = location.pathname === '/empresas';
  const isExportacionesActive = location.pathname.includes('/exportaciones');
  const isGestionEmpresaActive = location.pathname.includes('/empresas/') &&
                                  !location.pathname.includes('/comparativa') &&
                                  !location.pathname.includes('/exportaciones') &&
                                  !isResumenActive &&
                                  !isProyeccionActive &&
                                  !isSaludActive &&
                                  !isFinoviewActive &&
                                  !isHoldedActive &&
                                  selectedEmpresa &&
                                  location.pathname.includes(selectedEmpresa.id);

  const empresaBase = selectedEmpresa ? `/empresas/${selectedEmpresa.id}` : null;
  const irDesdePaleta = (accion) => {
    setPaletaOpen(false);
    accion();
  };

  // Menú superior: secciones en píldoras, cada una con su desplegable.
  const secciones = selectedEmpresa ? [
    {
      id: "analisis",
      label: "Análisis",
      testId: "ejercicios-menu",
      items: ejercicios.length === 0
        ? [{ id: "vacio", label: "Sin ejercicios importados", icon: Calendar, disabled: true }]
        : ejercicios.flatMap((ej) => {
            const pendientes = (ej.cuentas_pendientes || 0) + (ej.cuentas_error || 0);
            const activo = currentEjercicioId === ej.id;
            const filas = [{
              id: ej.id,
              label: `Ejercicio ${ej.anio}`,
              icon: Calendar,
              to: `/ejercicios/${ej.id}/analisis?tab=dashboard`,
              active: activo && !location.pathname.includes('/revision'),
              badge: pendientes > 0 && !activo ? pendientes : 0,
              testId: `ejercicio-${ej.anio}`,
            }];
            if (pendientes > 0 && activo) {
              filas.push({
                id: `${ej.id}-revision`,
                label: "Revisión cuentas",
                icon: AlertTriangle,
                to: `/ejercicios/${ej.id}/revision`,
                active: location.pathname.includes('/revision'),
                badge: pendientes,
                sub: true,
              });
            }
            return filas;
          }),
    },
    ...(ejercicios.length >= 1 ? [
      {
        id: "comparativas",
        label: "Comparativas",
        testId: "comparativa-link",
        items: [
          { id: "ratios", label: "Comparativa Ratios", icon: PieChart, to: `${empresaBase}/comparativa`, active: isComparativaRatiosActive, testId: "comparativa-ratios-link" },
          { id: "anios", label: "Comparativa Años", icon: GitCompare, to: `${empresaBase}/comparativa-anios`, active: isComparativaAniosActive, testId: "comparativa-anios-link" },
          { id: "flujos", label: "Flujos de Caja", icon: ArrowRightLeft, to: `${empresaBase}/comparativa-flujos`, active: isComparativaFlujosActive, testId: "comparativa-flujos-link" },
          // RESUMEN (Raúl 30-09: debajo de Flujos de Caja)
          { id: "resumen", label: "Resumen", icon: Sigma, to: `${empresaBase}/resumen`, active: isResumenActive, testId: "resumen-link" },
        ],
      },
      {
        id: "proyeccion",
        label: "Proyección",
        testId: "nav-proyeccion",
        items: [
          // La NOF ya no tiene entrada propia (petición de Pau, nov-2026): se
          // edita solo en Drivers → pestaña NOF. `?tab=nof` sigue funcionando.
          { id: "drivers", label: "Drivers", icon: TrendingUp, to: `${empresaBase}/proyeccion`, active: isProyeccionActive, testId: "proyeccion-link" },
        ],
      },
    ] : []),
    {
      id: "admin",
      label: "Administración",
      testId: "gestion-empresa-link",
      items: [
        { id: "gestion", label: "Gestión empresa", icon: Briefcase, to: empresaBase, active: isGestionEmpresaActive, testId: "gestion-empresa-item" },
        { id: "export", label: "Exportaciones", icon: Download, to: `${empresaBase}/exportaciones`, active: isExportacionesActive, testId: "exportaciones-link" },
        { id: "finoview", label: "Finoview (importar/exportar)", icon: Plug, to: `${empresaBase}/finoview`, active: isFinoviewActive, testId: "finoview-link" },
        { id: "holded", label: "Holded (contabilidad)", icon: BookOpen, to: `${empresaBase}/holded`, active: isHoldedActive, testId: "holded-link" },
        { id: "todas", label: "Todas las empresas", icon: Building, to: "/empresas", active: isEmpresasListPage },
        { id: "config", label: "Configuración", icon: Settings, disabled: true },
      ],
    },
  ] : [];

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-h-screen">
      {/* Lienzo abstracto de marca: es lo que se ve "a través" del cristal */}
      <div className="app-canvas" aria-hidden="true" />

      {/* Tour Guiado */}
      <GuidedTour />

      {/* Barra superior de cristal: logo · empresa · secciones · acciones */}
      <header className="sticky top-0 z-40 px-4 pt-3">
        <div className="app-topbar flex items-center gap-2 rounded-full py-1.5 pl-2 pr-2">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2 rounded-full py-1 pl-1.5 pr-3 hover:bg-foreground/[0.04]"
            aria-label="Finreport"
          >
            <img src="/images/PF_logo_transparente.png" alt="" className="h-7 w-auto" />
            <span className="hidden text-[15px] font-semibold tracking-tight xl:inline">Finreport</span>
          </Link>

          {/* Selector de empresa (con buscador) */}
          <Popover open={empresaPickerOpen} onOpenChange={setEmpresaPickerOpen}>
            <PopoverTrigger asChild>
              <button
                className="pill-group shrink-0 gap-2 !px-1.5 !py-1 pr-3 text-left hover:bg-[hsl(var(--glass)/0.85)]"
                data-testid="empresa-selector"
              >
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--pill-active))] text-xs font-semibold text-[hsl(var(--pill-active-foreground))]">
                  {(selectedEmpresa?.nombre || "?").trim().charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 max-w-[11rem]">
                  <span className="block truncate text-[13px] font-medium leading-tight">
                    {selectedEmpresa?.nombre || "Seleccionar empresa"}
                  </span>
                  {selectedEmpresa && (
                    <span className="block truncate text-[11px] leading-tight text-muted-foreground">
                      {[selectedEmpresa.nif, etiquetaRol(selectedEmpresa.my_role)].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0">
              <Command className="bg-transparent">
                <CommandInput placeholder="Buscar empresa o NIF" />
                <CommandList>
                  <CommandEmpty>No hay empresas que coincidan</CommandEmpty>
                  <CommandGroup>
                    {empresas.map((empresa) => (
                      <CommandItem
                        key={empresa.id}
                        value={`${empresa.nombre} ${empresa.nif || ""} ${empresa.id}`}
                        onSelect={() => {
                          setEmpresaPickerOpen(false);
                          handleSelectEmpresa(empresa);
                        }}
                      >
                        <Building2 className="text-muted-foreground" />
                        <span className="flex-1 truncate">{empresa.nombre}</span>
                        {selectedEmpresa?.id === empresa.id && <Check className="text-primary" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      value="todas las empresas gestionar"
                      onSelect={() => {
                        setEmpresaPickerOpen(false);
                        navigate('/empresas');
                      }}
                    >
                      <Building className="text-muted-foreground" />
                      Todas las empresas
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Secciones */}
          <nav className="pill-group min-w-0 overflow-x-auto" aria-label="Secciones">
            {selectedEmpresa ? (
              <>
                <button
                  onClick={() => navigate('/inicio')}
                  className={cn("pill", isInicioActive && "pill-active")}
                  aria-current={isInicioActive ? "page" : undefined}
                  data-testid="inicio-link"
                >
                  <Home className="h-3.5 w-3.5" />
                  Inicio
                </button>
                <button
                  onClick={() => navigate(`${empresaBase}/salud`)}
                  className={cn("pill", isSaludActive && "pill-active")}
                  aria-current={isSaludActive ? "page" : undefined}
                  data-testid="salud-link"
                >
                  <HeartPulse className="h-3.5 w-3.5" />
                  Salud
                </button>
                {secciones.map((s) => (
                  <NavMenu key={s.id} seccion={s} navigate={navigate} />
                ))}
              </>
            ) : (
              <button
                onClick={() => navigate('/empresas')}
                className={cn("pill", isEmpresasListPage && "pill-active")}
              >
                <Building className="h-3.5 w-3.5" />
                Todas las empresas
              </button>
            )}
          </nav>

          <div className="flex-1" />

          {/* Buscar o ir a… (Ctrl K) */}
          <button
            onClick={() => setPaletaOpen(true)}
            className="pill-group hidden shrink-0 gap-2 !px-3 !py-1.5 text-[13px] text-muted-foreground hover:text-foreground lg:inline-flex"
            data-testid="abrir-paleta"
            aria-label="Buscar o ir a"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Buscar…</span>
            <kbd className="rounded-md border border-border px-1.5 py-0.5 text-[10px]">Ctrl K</kbd>
          </button>

          <IconPill label={theme === "light" ? "Cristal oscuro" : "Cristal claro"} onClick={toggleTheme} testId="theme-toggle">
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </IconPill>
          <IconPill label="Tour guiado" onClick={() => window.dispatchEvent(new CustomEvent('startTour'))} testId="tour-button">
            <HelpCircle className="h-4 w-4" />
          </IconPill>

          {/* Más: Pool Bancario · Conectar IA · Diagnóstico (admin) */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button className="icon-pill shrink-0" aria-label="Más herramientas" data-testid="menu-herramientas">
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Más herramientas</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onSelect={() => window.location.assign(POOLBANCARIO_URL)} data-testid="switch-poolbancario">
                <Landmark className="mr-2 h-4 w-4 text-primary" />
                <span className="flex-1">Pool Bancario</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/conectar-ia')} data-testid="conectar-ia-link">
                <Sparkles className="mr-2 h-4 w-4" />
                Conectar con ChatGPT y Claude
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onSelect={() => navigate('/diagnostico')} data-testid="diagnostico-link">
                  <Activity className="mr-2 h-4 w-4" />
                  Diagnóstico
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="shrink-0">
            <NotificationBell />
          </div>
          <div className="flex shrink-0 items-center pr-1">
            <UserButton afterSignOutUrl="/sign-in" />
          </div>
        </div>
      </header>

      {/* Paleta de comandos (Ctrl+K) */}
      <Dialog open={paletaOpen} onOpenChange={setPaletaOpen}>
        <DialogContent className="max-w-xl overflow-hidden p-0" data-testid="paleta-comandos">
          <DialogTitle className="sr-only">Buscar o ir a</DialogTitle>
          <DialogDescription className="sr-only">
            Escribe para saltar a una empresa, un ejercicio o una pantalla.
          </DialogDescription>
          <Command className="bg-transparent">
            <CommandInput placeholder="Empresa, ejercicio o pantalla" />
            <CommandList className="max-h-[380px]">
              <CommandEmpty>Sin resultados</CommandEmpty>
              {selectedEmpresa && (
                <CommandGroup heading={selectedEmpresa.nombre}>
                  <CommandItem value="salud" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/salud`))}>
                    <HeartPulse /> Salud
                  </CommandItem>
                  {ejercicios.map((ej) => (
                    <CommandItem
                      key={ej.id}
                      value={`ejercicio ${ej.anio} analisis dashboard`}
                      onSelect={() => irDesdePaleta(() => navigate(`/ejercicios/${ej.id}/analisis?tab=dashboard`))}
                    >
                      <Calendar /> Ejercicio {ej.anio}
                    </CommandItem>
                  ))}
                  {ejercicios.length >= 1 && (
                    <>
                      <CommandItem value="comparativa ratios rentabilidad" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/comparativa`))}>
                        <PieChart /> Comparativa Ratios
                      </CommandItem>
                      <CommandItem value="comparativa años anios" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/comparativa-anios`))}>
                        <GitCompare /> Comparativa Años
                      </CommandItem>
                      <CommandItem value="flujos de caja comparativa" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/comparativa-flujos`))}>
                        <ArrowRightLeft /> Flujos de Caja
                      </CommandItem>
                      <CommandItem value="resumen" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/resumen`))}>
                        <Sigma /> Resumen
                      </CommandItem>
                      <CommandItem value="proyeccion drivers" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/proyeccion`))}>
                        <TrendingUp /> Proyección · Drivers
                      </CommandItem>
                    </>
                  )}
                  <CommandItem value="gestion empresa ejercicios importar" onSelect={() => irDesdePaleta(() => navigate(empresaBase))}>
                    <Briefcase /> Gestión empresa
                  </CommandItem>
                  <CommandItem value="exportaciones excel" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/exportaciones`))}>
                    <Download /> Exportaciones
                  </CommandItem>
                  <CommandItem value="finoview importar exportar" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/finoview`))}>
                    <Plug /> Finoview
                  </CommandItem>
                  <CommandItem value="holded contabilidad" onSelect={() => irDesdePaleta(() => navigate(`${empresaBase}/holded`))}>
                    <BookOpen /> Holded
                  </CommandItem>
                </CommandGroup>
              )}
              {empresas.length > 0 && (
                <CommandGroup heading="Cambiar de empresa">
                  {empresas.map((empresa) => (
                    <CommandItem
                      key={empresa.id}
                      value={`empresa ${empresa.nombre} ${empresa.nif || ""} ${empresa.id}`}
                      onSelect={() => irDesdePaleta(() => handleSelectEmpresa(empresa))}
                    >
                      <Building2 />
                      <span className="flex-1 truncate">{empresa.nombre}</span>
                      {selectedEmpresa?.id === empresa.id && <Check className="text-primary" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandGroup heading="General">
                <CommandItem value="todas las empresas" onSelect={() => irDesdePaleta(() => navigate('/empresas'))}>
                  <Building /> Todas las empresas
                </CommandItem>
                <CommandItem value="conectar ia chatgpt claude" onSelect={() => irDesdePaleta(() => navigate('/conectar-ia'))}>
                  <Sparkles /> Conectar IA
                </CommandItem>
                {isAdmin && (
                  <CommandItem value="diagnostico salud app" onSelect={() => irDesdePaleta(() => navigate('/diagnostico'))}>
                    <Activity /> Diagnóstico
                  </CommandItem>
                )}
                <CommandItem value="pool bancario" onSelect={() => irDesdePaleta(() => window.location.assign(POOLBANCARIO_URL))}>
                  <Landmark /> Pool Bancario
                </CommandItem>
                <CommandItem value="tema cristal claro oscuro modo" onSelect={() => irDesdePaleta(toggleTheme)}>
                  {theme === "light" ? <Moon /> : <Sun />} {theme === "light" ? "Cristal oscuro" : "Cristal claro"}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* Contenido */}
      <main className="relative z-10 min-h-screen">
        <div className="flex items-center justify-between gap-4 px-6 pb-1 pt-5">
          <Breadcrumbs empresa={selectedEmpresa} ejercicios={ejercicios} />
          <span className="shrink-0 rounded-full border border-border/80 px-2.5 py-0.5 text-[11px] text-muted-foreground">PGC Pymes</span>
        </div>
        <div className="px-6 pb-10 pt-3">
          <Outlet context={{ empresa: selectedEmpresa, ejercicios }} />
        </div>
      </main>
    </div>
    </TooltipProvider>
  );
}

function etiquetaRol(rol) {
  if (rol === "read") return "Compartida · lectura";
  if (rol === "write") return "Compartida · edición";
  return null;
}

function IconPill({ label, onClick, testId, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button onClick={onClick} className="icon-pill shrink-0" data-testid={testId} aria-label={label}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

// Píldora de sección con su desplegable. La píldora se marca activa si
// cualquiera de sus pantallas lo está.
function NavMenu({ seccion, navigate }) {
  const activa = seccion.items.some((it) => it.active);
  const avisos = seccion.items.reduce((n, it) => n + (it.badge || 0), 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={cn("pill", activa && "pill-active")} data-testid={seccion.testId}>
          {seccion.label}
          {avisos > 0 && !activa && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">{seccion.label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {seccion.items.map((it) => {
          const Icon = it.icon;
          return (
            <DropdownMenuItem
              key={it.id}
              disabled={it.disabled}
              onSelect={() => it.to && navigate(it.to)}
              data-testid={it.testId}
              className={cn("gap-2 rounded-lg", it.sub && "pl-7", it.active && "bg-foreground/[0.06] font-medium")}
            >
              <Icon className={cn("h-4 w-4", it.active ? "text-primary" : "text-muted-foreground")} />
              <span className="flex-1 truncate">{it.label}</span>
              {it.badge > 0 && (
                <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                  {it.badge}
                </span>
              )}
              {it.active && <Check className="h-3.5 w-3.5 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const PESTANAS_ANALISIS = {
  dashboard: "Dashboard",
  balance: "Balance",
  pyg: "PyG",
  ebitda: "EBITDA",
  flujos: "Flujos",
  margen: "Margen Bruto",
};

const PAGINAS_EJERCICIO = {
  revision: "Revisión",
  asientos: "Asientos manuales",
  importar: "Importar",
  contabilidad: "Contabilidad",
  efe: "Construcción del EFE",
};

const PAGINAS_EMPRESA = {
  salud: "Salud",
  comparativa: "Comparativa Ratios",
  "comparativa-anios": "Comparativa Años",
  "comparativa-flujos": "Flujos de Caja",
  resumen: "Resumen",
  proyeccion: "Proyección",
  exportaciones: "Exportaciones",
  finoview: "Finoview",
  holded: "Holded",
  dfn: "Deuda Financiera Neta",
};

const PAGINAS_SUELTAS = {
  inicio: "Inicio",
  invitaciones: "Invitaciones",
  "conectar-ia": "Conectar IA",
  diagnostico: "Diagnóstico",
};

function Breadcrumbs({ empresa, ejercicios }) {
  const location = useLocation();

  const breadcrumbs = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Pestaña activa del análisis (?tab=…). Se lee aquí, no fuera del memo:
    // el 2026-11-01 se borró la variable al quitar NOF del menú y la miga del
    // análisis lanzaba ReferenceError en producción (pantalla en blanco).
    const tab = new URLSearchParams(location.search).get('tab');
    const crumbs = [];

    if (pathParts[0] === 'empresas') {
      if (!pathParts[1]) {
        crumbs.push({ label: 'Empresas' });
      } else {
        crumbs.push({ label: empresa?.nombre || 'Empresa', path: `/empresas/${pathParts[1]}` });
        const pagina = PAGINAS_EMPRESA[pathParts[2]];
        if (!pathParts[2]) {
          crumbs.push({ label: 'Gestión empresa' });
        } else if (pathParts[2] === 'proyeccion') {
          crumbs.push({ label: 'Proyección' });
          crumbs.push({ label: 'Drivers' });
        } else {
          crumbs.push({ label: pagina || pathParts[2] });
        }
      }
    } else if (pathParts[0] === 'ejercicios') {
      if (empresa) crumbs.push({ label: empresa.nombre, path: `/empresas/${empresa.id}` });
      const ejercicio = ejercicios.find((e) => e.id === pathParts[1]);
      crumbs.push({
        label: ejercicio ? `Ejercicio ${ejercicio.anio}` : 'Ejercicio',
        path: pathParts[2] !== 'analisis' ? `/ejercicios/${pathParts[1]}/analisis?tab=dashboard` : undefined,
      });
      if (pathParts[2] === 'analisis') {
        crumbs.push({ label: PESTANAS_ANALISIS[tab] || 'Dashboard' });
      } else if (pathParts[2]) {
        crumbs.push({ label: PAGINAS_EJERCICIO[pathParts[2]] || pathParts[2] });
      }
    } else if (PAGINAS_SUELTAS[pathParts[0]]) {
      crumbs.push({ label: PAGINAS_SUELTAS[pathParts[0]] });
    }

    return crumbs;
  }, [location.pathname, location.search, empresa, ejercicios]);

  if (breadcrumbs.length === 0) return <div />;

  return (
    <nav aria-label="Ruta" className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
      {breadcrumbs.map((crumb, index) => {
        const ultimo = index === breadcrumbs.length - 1;
        return (
          <span key={index} className="flex min-w-0 items-center gap-1.5">
            {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-50" />}
            {crumb.path && !ultimo ? (
              <Link to={crumb.path} className="truncate transition-colors hover:text-foreground">
                {crumb.label}
              </Link>
            ) : (
              <span className={cn("truncate", ultimo && "font-medium text-foreground")}>
                {crumb.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
